import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, MapPin, Home, Info, FileText, Download, Percent } from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { StatusTimeline } from "@/components/StatusTimeline";
import { STATUS_COLORS, DOC_STATUS_COLORS } from "@/lib/constants";
import api, { downloadDoc } from "@/lib/api";

export default function ReferralDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ref, setRef] = useState(null);

  useEffect(() => {
    api.get(`/referrals/${id}`).then((r) => setRef(r.data)).catch(() => navigate("/dashboard"));
  }, [id, navigate]);

  if (!ref) return <DashboardLayout title="Segnalazione"><div className="text-slate-400">Caricamento…</div></DashboardLayout>;

  return (
    <DashboardLayout title={`Pratica ${ref.code}`}>
      <button onClick={() => navigate("/dashboard")} className="inline-flex items-center gap-2 text-sm text-slate-500 mb-6 hover:text-slate-800">
        <ArrowLeft className="h-4 w-4" /> Torna alla dashboard
      </button>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-1 space-y-4">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl border border-slate-200 bg-white p-6">
            <div className="flex items-center justify-between">
              <span className="font-mono font-semibold text-slate-900">{ref.code}</span>
              <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${STATUS_COLORS[ref.status] || "bg-slate-100"}`}>
                {ref.closed ? "Conclusa" : ref.status_label}
              </span>
            </div>
            <div className="mt-5 space-y-3 text-sm">
              <p className="flex items-center gap-2 text-slate-700"><Home className="h-4 w-4 text-slate-400" /> {ref.property_type}</p>
              <p className="flex items-center gap-2 text-slate-700"><MapPin className="h-4 w-4 text-slate-400" /> {ref.comune}{ref.zona ? ` · ${ref.zona}` : ""}</p>
              <p className="flex items-center gap-2 text-slate-500 text-xs">Inviata il {new Date(ref.created_at).toLocaleDateString("it-IT")}</p>
              {ref.commission_percentage && (
                <p className="flex items-center gap-2 text-slate-700" data-testid="reporter-commission-pct">
                  <Percent className="h-4 w-4 text-[#8a6d0f]" /> Merito fidelizzazione concordato: <strong className="text-[#8a6d0f]">{ref.commission_percentage}%</strong>
                </p>
              )}
            </div>
          </motion.div>

          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 flex gap-3">
            <Info className="h-5 w-5 text-slate-400 shrink-0" />
            <p className="text-xs text-slate-500 leading-relaxed">
              Per riservatezza, alcune informazioni relative a proprietario, acquirente e trattative
              non sono visibili. Vedi sempre lo stato aggiornato della pratica.
            </p>
          </div>

          {ref.documents && ref.documents.length > 0 && (
            <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="reporter-documents">
              <p className="text-xs font-semibold uppercase text-slate-400 mb-3 flex items-center gap-2"><FileText className="h-4 w-4" /> Documenti della pratica</p>
              <div className="space-y-2">
                {ref.documents.map((d) => (
                  <div key={d.id} className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm text-slate-700 truncate">{d.original_filename}</p>
                      <span className={`inline-block mt-1 rounded-full px-2 py-0.5 text-xs font-medium ${DOC_STATUS_COLORS[d.status] || "bg-slate-100 text-slate-600"}`}>{d.status_label}</span>
                    </div>
                    <button onClick={() => downloadDoc(d.id)} data-testid={`reporter-doc-download-${d.id}`}
                      className="shrink-0 text-slate-400 hover:text-[#8a6d0f]" title="Apri documento">
                      <Download className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {ref.closed && (
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <p className="text-sm text-slate-600">{ref.public_label}</p>
            </div>
          )}
        </div>

        <div className="lg:col-span-2">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
            <h2 className="font-display text-lg font-semibold text-slate-900 mb-6">Avanzamento della pratica</h2>
            {ref.closed ? (
              <p className="text-slate-500">{ref.public_label}</p>
            ) : (
              <StatusTimeline stages={ref.stages} current={ref.stage} closed={ref.closed} />
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
